import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Calendar, Phone, Video, Users, Search,
  Plus, Loader2, RefreshCcw, Clock, MapPin,
  CheckCircle, AlertCircle, Circle, Edit2, X, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

// Searchable Client Selector Component
const ClientSearchSelect = ({ clients, value, onChange, placeholder = "Search client by name or code..." }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Find selected client on mount or value change
  useEffect(() => {
    if (value) {
      const client = clients.find(c => c.client_code === value);
      setSelectedClient(client);
      if (client) {
        setSearchTerm(`${client.name || client.client_name} (${client.client_code})`);
      }
    } else {
      setSelectedClient(null);
      setSearchTerm('');
    }
  }, [value, clients]);

  // Filter clients based on search term
  const filteredClients = clients.filter(client => {
    const name = (client.name || client.client_name || '').toLowerCase();
    const code = (client.client_code || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || code.includes(search);
  }).slice(0, 50); // Limit to 50 results for performance

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (client) => {
    setSelectedClient(client);
    setSearchTerm(`${client.name || client.client_name} (${client.client_code})`);
    onChange(client.client_code);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedClient(null);
    setSearchTerm('');
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
            if (selectedClient && e.target.value !== `${selectedClient.name || selectedClient.client_name} (${selectedClient.client_code})`) {
              setSelectedClient(null);
              onChange('');
            }
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10 bg-slate-800 border-white/10"
          data-testid="client-search-input"
        />
        {(searchTerm || selectedClient) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-white/10 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredClients.length === 0 ? (
            <div className="p-3 text-sm text-slate-400 text-center">
              {searchTerm ? 'No clients found' : 'Type to search clients'}
            </div>
          ) : (
            filteredClients.map((client) => (
              <button
                key={client.client_code}
                type="button"
                onClick={() => handleSelect(client)}
                className={`w-full px-3 py-2 text-left hover:bg-white/10 transition-colors ${
                  selectedClient?.client_code === client.client_code ? 'bg-[#C5A059]/20 text-[#C5A059]' : 'text-white'
                }`}
                data-testid={`client-option-${client.client_code}`}
              >
                <div className="font-medium">{client.name || client.client_name}</div>
                <div className="text-xs text-slate-400">{client.client_code}</div>
              </button>
            ))
          )}
          {filteredClients.length === 50 && (
            <div className="p-2 text-xs text-slate-500 text-center border-t border-white/5">
              Showing first 50 results. Type more to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MeetingLogPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [newMeeting, setNewMeeting] = useState({
    client_code: '',
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_type: 'call',
    notes: '',
    outcome: '',
    follow_up_date: '',
    follow_up_status: 'pending'
  });

  const isPrincipal = user?.role === 'principal' || user?.role === 'business_head';

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/target-definition/meetings`;
      if (filterStatus !== 'all') {
        url += `?follow_up_status=${filterStatus}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMeetings(data);
        
        // Principal: mark meetings as viewed after fetching
        if (isPrincipal) {
          const unviewed = data.filter(m => !m.principal_viewed);
          if (unviewed.length > 0) {
            fetch(`${API_URL}/target-definition/meetings/mark-viewed`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          }
        }
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, isPrincipal]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not { clients: [...] }
        const clientsList = Array.isArray(data) ? data : (data.clients || []);
        setClients(clientsList);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchMeetings();
    if (!isPrincipal) {
      fetchClients();
    }
  }, [fetchMeetings, fetchClients, isPrincipal]);

  const handleAddMeeting = async () => {
    if (!newMeeting.client_code) {
      toast.error('Please select a client');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/meetings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newMeeting),
      });

      if (res.ok) {
        toast.success('Meeting logged successfully');
        setShowAddDialog(false);
        setNewMeeting({
          client_code: '',
          meeting_date: new Date().toISOString().split('T')[0],
          meeting_type: 'call',
          notes: '',
          outcome: '',
          follow_up_date: '',
          follow_up_status: 'pending'
        });
        fetchMeetings();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to log meeting');
      }
    } catch (err) {
      toast.error('Error logging meeting');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMeeting = async () => {
    if (!selectedMeeting) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/target-definition/meetings/${selectedMeeting.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          notes: selectedMeeting.notes,
          outcome: selectedMeeting.outcome,
          follow_up_date: selectedMeeting.follow_up_date,
          follow_up_status: selectedMeeting.follow_up_status
        }),
      });

      if (res.ok) {
        toast.success('Meeting updated');
        setShowEditDialog(false);
        setSelectedMeeting(null);
        fetchMeetings();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update meeting');
      }
    } catch (err) {
      toast.error('Error updating meeting');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting? This will also remove related incentives and pending activations.')) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/target-definition/meetings/${meetingId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        toast.success('Meeting deleted successfully');
        fetchMeetings();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete meeting');
      }
    } catch (err) {
      toast.error('Error deleting meeting');
    }
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="w-4 h-4 text-blue-400" />;
      case 'video': return <Video className="w-4 h-4 text-purple-400" />;
      case 'physical': return <MapPin className="w-4 h-4 text-amber-400" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getMeetingTypeBadge = (type) => {
    const colors = {
      call: 'bg-blue-600',
      video: 'bg-purple-600',
      physical: 'bg-amber-600'
    };
    const labels = {
      call: 'Call',
      video: 'Video',
      physical: 'Physical'
    };
    return (
      <Badge className={`${colors[type] || 'bg-slate-600'}`}>
        {getMeetingTypeIcon(type)}
        <span className="ml-1">{labels[type] || type}</span>
      </Badge>
    );
  };

  const getFollowUpStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-600', icon: <Circle className="w-3 h-3" />, label: 'Pending' },
      completed: { color: 'bg-emerald-600', icon: <CheckCircle className="w-3 h-3" />, label: 'Completed' },
      overdue: { color: 'bg-red-600', icon: <AlertCircle className="w-3 h-3" />, label: 'Overdue' }
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={c.color}>
        {c.icon}
        <span className="ml-1">{c.label}</span>
      </Badge>
    );
  };

  // Calculate stats
  const thisMonthMeetings = meetings.filter(m => {
    const meetingDate = new Date(m.meeting_date);
    const now = new Date();
    return meetingDate.getMonth() === now.getMonth() && meetingDate.getFullYear() === now.getFullYear();
  });

  const pendingFollowUps = meetings.filter(m => 
    m.follow_up_status === 'pending' && m.follow_up_date
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="meeting-log-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Meeting Log</h1>
          <p className="text-slate-400">Track client meetings and follow-ups</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 bg-slate-800 border-white/10">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Meetings</SelectItem>
              <SelectItem value="pending">Pending Follow-up</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchMeetings} className="border-[#C5A059]/30">
            <RefreshCcw className="w-4 h-4" />
          </Button>
          {!isPrincipal && (
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#C5A059] hover:bg-[#b4914e]">
              <Plus className="w-4 h-4 mr-2" /> Log Meeting
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-dark border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">This Month</p>
                <p className="text-2xl font-bold text-white">{thisMonthMeetings.length}</p>
              </div>
              <Calendar className="w-8 h-8 text-[#C5A059]" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Calls</p>
                <p className="text-2xl font-bold text-blue-400">
                  {thisMonthMeetings.filter(m => m.meeting_type === 'call').length}
                </p>
              </div>
              <Phone className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Video Calls</p>
                <p className="text-2xl font-bold text-purple-400">
                  {thisMonthMeetings.filter(m => m.meeting_type === 'video').length}
                </p>
              </div>
              <Video className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="card-dark border-white/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Pending Follow-ups</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {pendingFollowUps.length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meetings Table */}
      <Card className="card-dark border-white/5">
        <CardHeader>
          <CardTitle className="text-white">Meeting History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10">
                <TableHead className="text-slate-400">Date</TableHead>
                <TableHead className="text-slate-400">Client</TableHead>
                <TableHead className="text-slate-400">Type</TableHead>
                <TableHead className="text-slate-400">Notes</TableHead>
                <TableHead className="text-slate-400">Outcome</TableHead>
                <TableHead className="text-slate-400">Follow-up</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
                <TableHead className="text-slate-400">Activation</TableHead>
                {isPrincipal && <TableHead className="text-slate-400">Logged By</TableHead>}
                <TableHead className="text-right text-slate-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meetings.map((meeting) => (
                <TableRow key={meeting.id} className={`border-white/5 hover:bg-white/5 ${
                  isPrincipal && !meeting.principal_viewed ? 'bg-amber-500/5 border-l-2 border-l-amber-500' : ''
                }`}>
                  <TableCell className="text-white">
                    <div className="flex items-center gap-2">
                      {new Date(meeting.meeting_date).toLocaleDateString()}
                      {isPrincipal && !meeting.principal_viewed && (
                        <Badge className="bg-amber-500 text-white text-[10px] px-1.5 py-0">NEW</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white font-medium">{meeting.client_name}</span>
                      <span className="text-slate-500 text-xs block">{meeting.client_code}</span>
                    </div>
                  </TableCell>
                  <TableCell>{getMeetingTypeBadge(meeting.meeting_type)}</TableCell>
                  <TableCell className="text-slate-300 max-w-[150px] truncate" title={meeting.notes}>
                    {meeting.notes || '-'}
                  </TableCell>
                  <TableCell className="text-slate-300 max-w-[150px] truncate" title={meeting.outcome}>
                    {meeting.outcome || '-'}
                  </TableCell>
                  <TableCell className="text-slate-400">
                    {meeting.follow_up_date ? (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(meeting.follow_up_date).toLocaleDateString()}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {meeting.follow_up_date ? getFollowUpStatusBadge(meeting.follow_up_status) : '-'}
                  </TableCell>
                  <TableCell>
                    {meeting.activation_status === 'brokerage_generated' ? (
                      <Badge className="bg-emerald-600 text-white text-xs">
                        Brokerage Generated
                      </Badge>
                    ) : meeting.activation_status === 'pending' ? (
                      <Badge className="bg-yellow-600 text-white text-xs">
                        Awaiting Brokerage
                      </Badge>
                    ) : '-'}
                  </TableCell>
                  {isPrincipal && (
                    <TableCell className="text-slate-400">{meeting.user_name || '-'}</TableCell>
                  )}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedMeeting(meeting);
                          setShowEditDialog(true);
                        }}
                        className="hover:bg-white/10"
                        title="Edit meeting"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteMeeting(meeting.id)}
                        className="hover:bg-red-500/20 text-red-400"
                        title="Delete meeting"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {meetings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isPrincipal ? 9 : 8} className="text-center py-8 text-slate-400">
                    No meetings logged yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Meeting Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Log Meeting</DialogTitle>
            <DialogDescription>Record a client meeting or interaction</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <ClientSearchSelect
                clients={clients}
                value={newMeeting.client_code}
                onChange={(v) => setNewMeeting({ ...newMeeting, client_code: v })}
                placeholder="Search client by name or code..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meeting Date *</Label>
                <Input
                  type="date"
                  value={newMeeting.meeting_date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, meeting_date: e.target.value })}
                  className="bg-slate-800 border-white/10"
                />
              </div>
              <div>
                <Label>Meeting Type *</Label>
                <Select
                  value={newMeeting.meeting_type}
                  onValueChange={(v) => setNewMeeting({ ...newMeeting, meeting_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="physical">Physical Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={newMeeting.notes}
                onChange={(e) => setNewMeeting({ ...newMeeting, notes: e.target.value })}
                placeholder="Meeting notes..."
                className="bg-slate-800 border-white/10"
                rows={3}
              />
            </div>

            <div>
              <Label>Outcome</Label>
              <Textarea
                value={newMeeting.outcome}
                onChange={(e) => setNewMeeting({ ...newMeeting, outcome: e.target.value })}
                placeholder="Meeting outcome..."
                className="bg-slate-800 border-white/10"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Next Follow-up Date</Label>
                <Input
                  type="date"
                  value={newMeeting.follow_up_date}
                  onChange={(e) => setNewMeeting({ ...newMeeting, follow_up_date: e.target.value })}
                  className="bg-slate-800 border-white/10"
                />
              </div>
              <div>
                <Label>Follow-up Status</Label>
                <Select
                  value={newMeeting.follow_up_status}
                  onValueChange={(v) => setNewMeeting({ ...newMeeting, follow_up_status: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddMeeting} 
              disabled={saving}
              className="bg-[#C5A059] hover:bg-[#b4914e]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Log Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Meeting Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Update Meeting</DialogTitle>
            <DialogDescription>
              {selectedMeeting?.client_name} - {selectedMeeting?.meeting_date && new Date(selectedMeeting.meeting_date).toLocaleDateString()}
            </DialogDescription>
          </DialogHeader>

          {selectedMeeting && (
            <div className="space-y-4">
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={selectedMeeting.notes || ''}
                  onChange={(e) => setSelectedMeeting({ ...selectedMeeting, notes: e.target.value })}
                  placeholder="Meeting notes..."
                  className="bg-slate-800 border-white/10"
                  rows={3}
                />
              </div>

              <div>
                <Label>Outcome</Label>
                <Textarea
                  value={selectedMeeting.outcome || ''}
                  onChange={(e) => setSelectedMeeting({ ...selectedMeeting, outcome: e.target.value })}
                  placeholder="Meeting outcome..."
                  className="bg-slate-800 border-white/10"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Follow-up Date</Label>
                  <Input
                    type="date"
                    value={selectedMeeting.follow_up_date || ''}
                    onChange={(e) => setSelectedMeeting({ ...selectedMeeting, follow_up_date: e.target.value })}
                    className="bg-slate-800 border-white/10"
                  />
                </div>
                <div>
                  <Label>Follow-up Status</Label>
                  <Select
                    value={selectedMeeting.follow_up_status || 'pending'}
                    onValueChange={(v) => setSelectedMeeting({ ...selectedMeeting, follow_up_status: v })}
                  >
                    <SelectTrigger className="bg-slate-800 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowEditDialog(false); setSelectedMeeting(null); }}>Cancel</Button>
            <Button 
              onClick={handleUpdateMeeting} 
              disabled={saving}
              className="bg-[#C5A059] hover:bg-[#b4914e]"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Update Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingLogPage;
